import { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Button,
  Input,
  Spinner,
  Field,
  Checkbox,
  TabList,
  Tab,
  SelectTabData,
  Badge,
  RadioGroup,
  Radio,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  ReceiptMoneyRegular,
  SearchRegular,
  PersonRegular,
  BuildingRegular,
  PersonAddRegular,
  DeleteRegular,
  AddRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import XeroContactVerificationDialog from './XeroContactVerificationDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTaxReturns,
  addReturnToAppointment,
  createSharedInvoice,
  createDataverseContact,
  createDataverseAccount,
} from '../services/api';
import type {
  TaxReturn,
  AddReturnToAppointmentRequest,
  CreateSharedInvoiceRequest,
} from '../types';
import { getScheduleCBusinesses } from '../types';

interface CreateSharedInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentReturn: TaxReturn;
  appointmentReturns: TaxReturn[];
  onCreated: () => void;
}

type AddMode = 'search' | 'create';

export default function CreateSharedInvoiceDialog({
  open,
  onOpenChange,
  currentReturn,
  appointmentReturns,
  onCreated,
}: CreateSharedInvoiceDialogProps) {
  const queryClient = useQueryClient();

  // Selected returns state - Map for easy lookup
  const [selectedReturns, setSelectedReturns] = useState<Map<string, TaxReturn>>(
    new Map()
  );
  const [initialized, setInitialized] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<TaxReturn[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Add mode state
  const [addMode, setAddMode] = useState<AddMode>('search');
  const [newReturnType, setNewReturnType] = useState<'individual' | 'business'>('individual');

  // Create new return form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Invoice amount state - per-return amounts
  const [returnAmounts, setReturnAmounts] = useState<Map<string, string>>(new Map());
  const [invoiceRecipientId, setInvoiceRecipientId] = useState<string>('');
  // Optional: when the recipient is billed via one of its Schedule C businesses (Dataverse account id).
  const [invoiceScheduleCBusinessId, setInvoiceScheduleCBusinessId] = useState<string | undefined>(undefined);
  const [overrideAmount, setOverrideAmount] = useState('');

  // Xero contact verification state
  const [xeroContactDialogOpen, setXeroContactDialogOpen] = useState(false);

  // Initialize selected returns when dialog opens
  if (open && !initialized) {
    const initialSelected = new Map<string, TaxReturn>();

    // Always include current return
    initialSelected.set(currentReturn.id, currentReturn);

    // Pre-select all appointment returns
    appointmentReturns.forEach(ret => {
      initialSelected.set(ret.id, ret);
    });

    // Initialize amounts from extracted invoice data
    const initialAmounts = new Map<string, string>();
    initialSelected.forEach((ret, id) => {
      const amount = ret.extractedInvoiceData?.totalAmount || 0;
      initialAmounts.set(id, amount > 0 ? amount.toString() : '');
    });
    setReturnAmounts(initialAmounts);
    setInvoiceRecipientId(currentReturn.id); // Default to current return as recipient

    setSelectedReturns(initialSelected);
    setInitialized(true);
  }

  // Reset state when dialog closes
  const handleOpenChange = (_: unknown, data: { open: boolean }) => {
    if (!data.open) {
      setInitialized(false);
      setSelectedReturns(new Map());
      setSearchTerm('');
      setSearchResults([]);
      setHasSearched(false);
      setAddMode('search');
      setNewReturnType('individual');
      setFirstName('');
      setLastName('');
      setCompanyName('');
      setEmail('');
      setPhone('');
      setOverrideAmount('');
      setReturnAmounts(new Map());
      setInvoiceRecipientId('');
      setInvoiceScheduleCBusinessId(undefined);
      setCreateError(null);
    }
    onOpenChange(data.open);
  };

  // Calculate totals
  const selectedReturnsList = useMemo(() => Array.from(selectedReturns.values()), [selectedReturns]);
  const totalFromReturns = useMemo(() => {
    let sum = 0;
    returnAmounts.forEach(amountStr => {
      sum += parseFloat(amountStr) || 0;
    });
    return sum;
  }, [returnAmounts]);
  const finalAmount = overrideAmount ? parseFloat(overrideAmount) : totalFromReturns;

  // Search for tax returns
  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    setHasSearched(false);
    try {
      const results = await getTaxReturns({ taxYear: currentReturn.taxYear });
      const searchLower = searchTerm.toLowerCase();
      const filtered = results
        .filter(
          (tr) =>
            tr.clientName.toLowerCase().includes(searchLower) &&
            !selectedReturns.has(tr.id) &&
            tr.id !== currentReturn.id
        )
        .slice(0, 10);
      setSearchResults(filtered);
      setHasSearched(true);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  }, [searchTerm, currentReturn.taxYear, currentReturn.id, selectedReturns]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Add a return to selection
  const handleAddReturn = (ret: TaxReturn) => {
    setSelectedReturns((prev) => {
      const newMap = new Map(prev);
      newMap.set(ret.id, ret);
      return newMap;
    });
    // Initialize the amount for the new return
    setReturnAmounts((prev) => {
      const newMap = new Map(prev);
      const amount = ret.extractedInvoiceData?.totalAmount || 0;
      newMap.set(ret.id, amount > 0 ? amount.toString() : '');
      return newMap;
    });
    // Remove from search results
    setSearchResults((prev) => prev.filter((r) => r.id !== ret.id));
  };

  // Remove a return from selection
  const handleRemoveReturn = (retId: string) => {
    if (retId === currentReturn.id) return; // Can't remove current return
    setSelectedReturns((prev) => {
      const newMap = new Map(prev);
      newMap.delete(retId);
      return newMap;
    });
    // Remove the amount for this return
    setReturnAmounts((prev) => {
      const newMap = new Map(prev);
      newMap.delete(retId);
      return newMap;
    });
    // If removed return was the invoice recipient, reset to current return
    if (retId === invoiceRecipientId) {
      setInvoiceRecipientId(currentReturn.id);
      setInvoiceScheduleCBusinessId(undefined);
    }
  };

  // Add return to appointment mutation
  const addReturnMutation = useMutation({
    mutationFn: async (request: AddReturnToAppointmentRequest) => {
      return addReturnToAppointment(currentReturn.appointmentId, request);
    },
    onSuccess: (newReturn) => {
      // Add the newly created return to selection
      setSelectedReturns((prev) => {
        const newMap = new Map(prev);
        newMap.set(newReturn.id, newReturn);
        return newMap;
      });
      // Reset create form
      setFirstName('');
      setLastName('');
      setCompanyName('');
      setEmail('');
      setPhone('');
      setAddMode('search');
      setCreateError(null);
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      queryClient.invalidateQueries({ queryKey: ['appointmentReturns', currentReturn.appointmentId] });
    },
    onError: (error: Error) => {
      setCreateError(error.message || 'Failed to create return');
    },
  });

  // Create new return and add to selection
  const handleCreateAndAdd = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      if (newReturnType === 'individual') {
        // Create contact
        const contact = await createDataverseContact({
          firstName,
          lastName,
          email: email || undefined,
          phone: phone || undefined,
        });

        // Add return with new contact
        const request: AddReturnToAppointmentRequest = {
          returnType: newReturnType,
          contactId: contact.contactId,
          parentReturnId: currentReturn.id,
          taxYear: currentReturn.taxYear,
        };
        addReturnMutation.mutate(request);
      } else {
        // Create account
        const account = await createDataverseAccount({
          name: companyName,
          email: email || undefined,
          phone: phone || undefined,
        });

        // Add return with new account
        const request: AddReturnToAppointmentRequest = {
          returnType: newReturnType,
          accountId: account.accountId,
          parentReturnId: currentReturn.id,
          taxYear: currentReturn.taxYear,
        };
        addReturnMutation.mutate(request);
      }
    } catch (error) {
      console.error('Create failed:', error);
      setCreateError(error instanceof Error ? error.message : 'Failed to create record');
    } finally {
      setIsCreating(false);
    }
  };

  // Create shared invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: async (params?: { xeroContactId?: string; forceCreateContact?: boolean }) => {
      const taxReturnIds = Array.from(selectedReturns.keys());

      // Build allocations from custom amounts
      const allocations = taxReturnIds
        .map(id => ({
          taxReturnId: id,
          allocatedAmount: parseFloat(returnAmounts.get(id) || '0') || 0,
        }))
        .filter(a => a.allocatedAmount > 0);

      // Build line items - one per return with custom amount
      const lineItems = taxReturnIds
        .filter(id => parseFloat(returnAmounts.get(id) || '0') > 0)
        .map(id => {
          const ret = selectedReturns.get(id)!;
          const amount = parseFloat(returnAmounts.get(id) || '0');
          return {
            description: `Tax preparation - ${ret.clientName} (${ret.taxYear})`,
            quantity: 1,
            unitPrice: amount,
            amount: amount,
          };
        });

      const request: CreateSharedInvoiceRequest = {
        taxReturnIds,
        lineItems: lineItems.length > 0 ? lineItems : undefined,
        allocations: allocations.length > 0 ? allocations : undefined,
        createInXero: true,
        primaryTaxReturnId: invoiceRecipientId, // Specify which return's client is invoice recipient
        xeroContactId: params?.xeroContactId,
        forceCreateContact: params?.forceCreateContact,
        scheduleCBusinessId: invoiceScheduleCBusinessId,
      };

      // If override amount is specified, use a single line item instead
      if (overrideAmount && parseFloat(overrideAmount) > 0) {
        request.lineItems = [{
          description: 'Tax Preparation Services',
          quantity: 1,
          unitPrice: parseFloat(overrideAmount),
          amount: parseFloat(overrideAmount),
        }];
        request.allocations = undefined; // Clear allocations when using override
      }

      return createSharedInvoice(request);
    },
    onSuccess: () => {
      // Invalidate specific queries for the current return
      queryClient.invalidateQueries({ queryKey: ['taxReturn', currentReturn.id] });
      queryClient.invalidateQueries({ queryKey: ['sharedInvoice', currentReturn.id] });

      // Invalidate all selected returns
      Array.from(selectedReturns.keys()).forEach(returnId => {
        queryClient.invalidateQueries({ queryKey: ['taxReturn', returnId] });
        queryClient.invalidateQueries({ queryKey: ['sharedInvoice', returnId] });
      });

      onCreated();
      onOpenChange(false);
    },
  });

  const canCreateNew =
    newReturnType === 'individual'
      ? firstName.trim() && lastName.trim()
      : companyName.trim();

  const getReturnLabel = (ret: TaxReturn) => {
    const isCurrentReturn = ret.id === currentReturn.id;
    const isAppointmentReturn = appointmentReturns.some((ar) => ar.id === ret.id);
    const label = isCurrentReturn
      ? 'This return'
      : isAppointmentReturn
      ? 'Same appt'
      : 'Added via search';
    return label;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogSurface style={{ maxWidth: '600px', maxHeight: '90vh' }}>
        <DialogBody style={{ overflow: 'auto' }}>
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ReceiptMoneyRegular />
              Create Shared Invoice
            </div>
          </DialogTitle>
          <DialogContent>
            {/* Selected Returns List */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '12px' }}>Returns to Include:</h4>
              <div
                style={{
                  border: '1px solid var(--tl-border)',
                  borderRadius: '8px',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                {selectedReturnsList.map((ret) => {
                  const isCurrentReturn = ret.id === currentReturn.id;
                  const amountValue = returnAmounts.get(ret.id) || '';
                  return (
                    <div
                      key={ret.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--tl-bg-subtle)',
                        backgroundColor: isCurrentReturn ? '#f0f9ff' : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Checkbox
                          checked={true}
                          disabled={isCurrentReturn}
                          onChange={() => handleRemoveReturn(ret.id)}
                        />
                        {ret.returnType === 'business' ? (
                          <BuildingRegular style={{ color: '#107c10' }} />
                        ) : (
                          <PersonRegular style={{ color: '#0078d4' }} />
                        )}
                        <div>
                          <div style={{ fontWeight: 500 }}>{ret.clientName}</div>
                          <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                            {ret.returnType === 'business'
                              ? 'Business'
                              : ret.filingType === 'joint'
                              ? 'MFJ'
                              : '1040'}{' '}
                            • TY {ret.taxYear} • {getReturnLabel(ret)}
                            {isCurrentReturn && ' *'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Input
                          type="number"
                          value={amountValue}
                          onChange={(_, data) => {
                            setReturnAmounts(prev => {
                              const newMap = new Map(prev);
                              newMap.set(ret.id, data.value);
                              return newMap;
                            });
                          }}
                          placeholder="0.00"
                          contentBefore={<span>$</span>}
                          style={{ width: '100px' }}
                          size="small"
                        />
                        {!isCurrentReturn && (
                          <Button
                            appearance="subtle"
                            icon={<DeleteRegular />}
                            size="small"
                            onClick={() => handleRemoveReturn(ret.id)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)', marginTop: '4px' }}>
                * Current return (required)
              </div>
            </div>

            {/* Add More Returns Section */}
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'var(--tl-border)',
                  }}
                />
                <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Add More Returns</span>
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'var(--tl-border)',
                  }}
                />
              </div>

              <TabList
                selectedValue={addMode}
                onTabSelect={(_, data: SelectTabData) => setAddMode(data.value as AddMode)}
                size="small"
                style={{ marginBottom: '12px' }}
              >
                <Tab value="search" icon={<SearchRegular />}>
                  Search Existing
                </Tab>
                <Tab value="create" icon={<PersonAddRegular />}>
                  Create New
                </Tab>
              </TabList>

              {/* Search Existing Tab */}
              {addMode === 'search' && (
                <div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <Input
                      placeholder="Search by client name..."
                      value={searchTerm}
                      onChange={(_, data) => setSearchTerm(data.value)}
                      onKeyDown={handleKeyDown}
                      contentBefore={<SearchRegular />}
                      style={{ flex: 1 }}
                    />
                    <Button
                      appearance="primary"
                      onClick={handleSearch}
                      disabled={isSearching || !searchTerm.trim()}
                    >
                      {isSearching ? <Spinner size="tiny" /> : 'Search'}
                    </Button>
                  </div>

                  {/* Search Results */}
                  {hasSearched && searchResults.length === 0 && (
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: 'var(--tl-bg-subtle)',
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: 'var(--tl-text-secondary)',
                      }}
                    >
                      No matching returns found for "{searchTerm}"
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div>
                      <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', marginBottom: '8px' }}>
                        Search Results:
                      </div>
                      <div
                        style={{
                          border: '1px solid var(--tl-border)',
                          borderRadius: '8px',
                          maxHeight: '150px',
                          overflow: 'auto',
                        }}
                      >
                        {searchResults.map((ret) => (
                          <div
                            key={ret.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              borderBottom: '1px solid var(--tl-bg-subtle)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {ret.returnType === 'business' ? (
                                <BuildingRegular style={{ color: '#107c10', fontSize: '16px' }} />
                              ) : (
                                <PersonRegular style={{ color: '#0078d4', fontSize: '16px' }} />
                              )}
                              <span>{ret.clientName}</span>
                              <Badge size="small" appearance="outline">
                                {ret.returnType === 'business' ? 'Business' : ret.filingType}
                              </Badge>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                                {ret.extractedInvoiceData?.totalAmount
                                  ? `$${ret.extractedInvoiceData.totalAmount.toFixed(2)}`
                                  : '-'}
                              </span>
                              <Button
                                appearance="primary"
                                icon={<AddRegular />}
                                size="small"
                                onClick={() => handleAddReturn(ret)}
                              >
                                Add
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Create New Tab */}
              {addMode === 'create' && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                      Return Type:
                    </div>
                    <RadioGroup
                      value={newReturnType}
                      onChange={(_, data) =>
                        setNewReturnType(data.value as 'individual' | 'business')
                      }
                      layout="horizontal"
                    >
                      <Radio
                        value="individual"
                        label={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <PersonRegular />
                            Individual (1040)
                          </div>
                        }
                      />
                      <Radio
                        value="business"
                        label={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <BuildingRegular />
                            Business
                          </div>
                        }
                      />
                    </RadioGroup>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {newReturnType === 'individual' ? (
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <Field label="First Name" required style={{ flex: 1 }}>
                          <Input
                            value={firstName}
                            onChange={(_, data) => setFirstName(data.value)}
                            placeholder="Enter first name"
                          />
                        </Field>
                        <Field label="Last Name" required style={{ flex: 1 }}>
                          <Input
                            value={lastName}
                            onChange={(_, data) => setLastName(data.value)}
                            placeholder="Enter last name"
                          />
                        </Field>
                      </div>
                    ) : (
                      <Field label="Company Name" required>
                        <Input
                          value={companyName}
                          onChange={(_, data) => setCompanyName(data.value)}
                          placeholder="Enter company name"
                        />
                      </Field>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <Field label="Email" style={{ flex: 1 }}>
                        <Input
                          type="email"
                          value={email}
                          onChange={(_, data) => setEmail(data.value)}
                          placeholder="(optional)"
                        />
                      </Field>
                      <Field label="Phone" style={{ flex: 1 }}>
                        <Input
                          type="tel"
                          value={phone}
                          onChange={(_, data) => setPhone(data.value)}
                          placeholder="(optional)"
                        />
                      </Field>
                    </div>
                  </div>

                  {createError && (
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#fde7e9',
                        borderRadius: '4px',
                        color: '#a4262c',
                        marginTop: '12px',
                      }}
                    >
                      <WarningRegular style={{ marginRight: '6px' }} />
                      {createError}
                    </div>
                  )}

                  <div style={{ marginTop: '12px' }}>
                    <Button
                      appearance="primary"
                      icon={<AddRegular />}
                      onClick={handleCreateAndAdd}
                      disabled={isCreating || addReturnMutation.isPending || !canCreateNew}
                    >
                      {isCreating || addReturnMutation.isPending ? (
                        <>
                          <Spinner size="tiny" style={{ marginRight: '8px' }} />
                          Creating...
                        </>
                      ) : (
                        'Create & Add to Invoice'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Details Section */}
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'var(--tl-border)',
                  }}
                />
                <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>Invoice Details</span>
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'var(--tl-border)',
                  }}
                />
              </div>

              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--tl-bg-subtle)',
                  borderRadius: '8px',
                }}
              >
                <Field label="Invoice Recipient" required style={{ marginBottom: '16px' }}>
                  {(() => {
                    const recipientReturn = selectedReturnsList.find(r => r.id === invoiceRecipientId);
                    const selectedBiz = invoiceScheduleCBusinessId && recipientReturn
                      ? getScheduleCBusinesses(recipientReturn).find(b => b.id === invoiceScheduleCBusinessId)
                      : undefined;
                    const selectedText = recipientReturn
                      ? (selectedBiz ? `${recipientReturn.clientName} → ${selectedBiz.name} (Schedule C)` : recipientReturn.clientName)
                      : '';
                    const compositeValue = invoiceScheduleCBusinessId
                      ? `${invoiceRecipientId}::${invoiceScheduleCBusinessId}`
                      : invoiceRecipientId;
                    return (
                      <Dropdown
                        value={selectedText}
                        selectedOptions={[compositeValue]}
                        onOptionSelect={(_, data) => {
                          const [rid, bid] = (data.optionValue as string).split('::');
                          setInvoiceRecipientId(rid);
                          setInvoiceScheduleCBusinessId(bid || undefined);
                        }}
                        style={{ width: '100%' }}
                      >
                        {selectedReturnsList.flatMap(ret => {
                          const clientText = `${ret.clientName} (${ret.returnType === 'business' ? 'Business' : '1040'} • TY ${ret.taxYear})`;
                          const opts = [
                            <Option key={ret.id} value={ret.id} text={clientText}>{clientText}</Option>,
                          ];
                          // Only Dataverse-linked businesses (with an account id) can resolve a Xero contact.
                          for (const biz of getScheduleCBusinesses(ret)) {
                            if (!biz.id || !biz.name) continue;
                            const bizText = `${ret.clientName} → ${biz.name} (Schedule C)`;
                            opts.push(
                              <Option key={`${ret.id}::${biz.id}`} value={`${ret.id}::${biz.id}`} text={bizText}>{bizText}</Option>
                            );
                          }
                          return opts;
                        })}
                      </Dropdown>
                    );
                  })()}
                </Field>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <span>Selected returns:</span>
                  <span style={{ fontWeight: 500 }}>{selectedReturnsList.length}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <span>Total from returns:</span>
                  <span style={{ fontWeight: 500, color: totalFromReturns > 0 ? '#107c10' : 'var(--tl-text-tertiary)' }}>
                    ${totalFromReturns.toFixed(2)}
                  </span>
                </div>

                <Field label="Override Amount" hint="Leave blank to use total from returns">
                  <Input
                    type="number"
                    value={overrideAmount}
                    onChange={(_, data) => setOverrideAmount(data.value)}
                    placeholder="0.00"
                    contentBefore={<span>$</span>}
                  />
                </Field>

                {finalAmount > 0 && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#dff6dd',
                      borderRadius: '8px',
                      border: '1px solid #107c10',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>Invoice Total:</span>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: '#107c10' }}>
                      ${finalAmount.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Error display */}
            {createInvoiceMutation.isError && (
              <div
                style={{
                  padding: '12px',
                  marginTop: '16px',
                  backgroundColor: '#fde7e9',
                  borderRadius: '4px',
                  color: '#a4262c',
                }}
              >
                <WarningRegular style={{ marginRight: '6px' }} />
                Failed to create invoice. Please try again.
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<ReceiptMoneyRegular />}
              onClick={() => setXeroContactDialogOpen(true)}
              disabled={
                createInvoiceMutation.isPending ||
                selectedReturnsList.length < 1
              }
            >
              {createInvoiceMutation.isPending ? (
                <>
                  <Spinner size="tiny" style={{ marginRight: '8px' }} />
                  Creating...
                </>
              ) : (
                'Create Invoice in Xero'
              )}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>

    </Dialog>

    {/* Xero Contact Verification - shown before creating shared invoice */}
    <XeroContactVerificationDialog
      open={xeroContactDialogOpen}
      onOpenChange={setXeroContactDialogOpen}
      clientName={(() => {
        const r = selectedReturns.get(invoiceRecipientId);
        if (invoiceScheduleCBusinessId && r) {
          const biz = getScheduleCBusinesses(r).find(b => b.id === invoiceScheduleCBusinessId);
          if (biz?.name) return biz.name;
        }
        return r?.clientName || currentReturn.clientName;
      })()}
      clientEmail={selectedReturns.get(invoiceRecipientId)?.clientEmail}
      taxReturnId={invoiceRecipientId || currentReturn.id}
      scheduleCBusinessId={invoiceScheduleCBusinessId}
      onConfirm={(xeroContactId) => {
        setXeroContactDialogOpen(false);
        createInvoiceMutation.mutate({
          xeroContactId: xeroContactId ?? undefined,
          forceCreateContact: xeroContactId === null,
        });
      }}
    />
    </>
  );
}
